import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { ARENA_BOTTOM, SCENE_H, SCENE_W } from '@rekrut/shared';
import type { MatchState } from '../sim/state.js';

const TOP = ARENA_BOTTOM;

/**
 * Подвал: прогресс лайков и лента событий.
 *
 * Прайс оружия отсюда переехал в плашку правого верхнего угла (PricePanel):
 * подвал зритель почти не смотрит, а решение «что отправить» принимается в
 * первые секунды, пока взгляд держится на верхней части экрана.
 */
export class Footer {
  readonly view = new Container();

  private readonly bg = new Graphics();
  private readonly likeLabel: Text;
  private readonly tickerRow: Text;
  private last = new Map<string, string>();
  /**
   * Альбомный режим. Полоса подвала в портрете занимает 250 пикселей внизу,
   * но в повёрнутом кадре эта зона приходится на правый край поля, где идёт
   * бой. Поэтому подвал сжимается в тонкую полупрозрачную ленту у нижней
   * кромки: лайки и лента событий остаются, картинку боя они не закрывают.
   */
  private land = false;

  constructor() {
    const mk = (size: number, weight: string, color: number): Text =>
      new Text({
        text: '',
        style: new TextStyle({
          fontFamily: 'system-ui, "Noto Color Emoji", sans-serif',
          fontSize: size,
          fontWeight: weight as never,
          fill: color,
          stroke: { color: 0x05070c, width: Math.max(2, size * 0.11) },
        }),
      });

    this.likeLabel = mk(22, '800', 0xff8fa8);
    this.tickerRow = mk(23, '700', 0xc9d6ea);

    this.view.addChild(this.bg, this.likeLabel, this.tickerRow);
    // Фон должен быть под всем остальным.
    this.view.setChildIndex(this.bg, 0);
  }

  setLandscape(on: boolean): void {
    this.land = on;
  }

  update(s: MatchState): void {
    const g = this.bg;
    g.clear();

    // В альбоме экран 1920×1080, в портрете 1080×1920.
    const width = this.land ? SCENE_H : SCENE_W;
    const bottom = this.land ? SCENE_W : SCENE_H;
    const top = this.land ? bottom - 96 : TOP;

    g.rect(0, top, width, bottom - top).fill({ color: 0x070a12, alpha: this.land ? 0.55 : 0.95 });
    g.moveTo(0, top).lineTo(width, top).stroke({ color: 0x1c2436, width: 2 });

    this.likeLabel.position.set(38, top + (this.land ? 12 : 22));
    this.tickerRow.position.set(38, top + (this.land ? 56 : 92));

    const w = width - 76;
    const y = top + (this.land ? 42 : 56);
    const frac = Math.max(0, Math.min(1, s.likeCounter / Math.max(1, s.likeThreshold)));
    g.roundRect(38, y, w, 10, 5).fill({ color: 0x1a2131 });
    g.roundRect(38, y, w * frac, 10, 5).fill({ color: 0xff5c86 });

    this.set(this.likeLabel, 'likes', `❤ ${Math.floor(s.likeCounter)} / ${s.likeThreshold} до щита`);
    this.updateTicker(s);
  }

  private updateTicker(s: MatchState): void {
    const item = s.ticker[s.ticker.length - 1];
    if (!item) {
      this.tickerRow.visible = false;
      return;
    }
    this.tickerRow.visible = true;
    this.set(this.tickerRow, 'tk', item.text);
    this.tickerRow.style.fill = item.color;
  }

  private set(node: Text, key: string, value: string): void {
    if (this.last.get(key) === value) return;
    this.last.set(key, value);
    node.text = value;
  }
}
